import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { t } from '../lib/i18n';
import { messageFor } from '../lib/errors';
import { formatBytes, formatDate, formatDateTime, formatNumber } from '../ui/format';
import { Badge, ErrorState, Loading, PageHeader, Panel, type BadgeTone } from '../ui/Page';
import { ConfirmAction } from '../ui/ConfirmAction';
import { InvoicesPanel } from './InvoicesPanel';
import { bandTone } from './ProductPanel';
import { tenantPulse } from './api';
import {
  changePlan,
  endSupportSession,
  extendTrial,
  getTenant,
  listFeatures,
  listPlans,
  listSupportSessions,
  overrideFeature,
  overrideLimit,
  revokeOverride,
  setSubscriptionStatus,
  setTenantStatus,
  startSupportSession,
  type OverrideRow,
} from './api';

/**
 * One tenant, from Safe Cortex's side.
 *
 * Every action here is something the customer feels, taken by somebody who is not the customer. So
 * each one goes through a dialog that requires a reason, and the reason is what the API stores —
 * §17 is explicit that support access and administrative intervention are never silent.
 *
 * Support access is the sharpest of them: it is read-only by default, time-limited, and the tenant
 * can see it in their own product while it is open.
 */

type Action =
  | { kind: 'suspend' }
  | { kind: 'restore' }
  | { kind: 'readonly' }
  | { kind: 'plan' }
  | { kind: 'trial' }
  | { kind: 'subscription' }
  | { kind: 'support' }
  | { kind: 'revoke-override'; override: OverrideRow }
  | { kind: 'feature-override' }
  | { kind: 'limit-override' }
  | null;

const STATUS_TONE: Record<string, BadgeTone> = {
  ACTIVE: 'positive',
  READ_ONLY: 'warning',
  SUSPENDED: 'danger',
  DELETED: 'neutral',
};

export function TenantPage(): React.JSX.Element {
  const { tenantId } = useParams<{ tenantId: string }>();
  const id = tenantId!;
  const client = useQueryClient();

  const [action, setAction] = useState<Action>(null);
  const [error, setError] = useState<string | null>(null);
  const [planKey, setPlanKey] = useState('');
  const [days, setDays] = useState(7);
  const [subscriptionStatus, setSubscriptionStatusValue] = useState('ACTIVE');
  const [readOnly, setReadOnly] = useState(true);
  const [minutes, setMinutes] = useState(60);
  const [issuedToken, setIssuedToken] = useState<string | null>(null);
  const [overrideKey, setOverrideKey] = useState('');
  const [featureOn, setFeatureOn] = useState(true);
  const [limitValue, setLimitValue] = useState('');
  const [overrideDays, setOverrideDays] = useState(0);

  const tenant = useQuery({ queryKey: ['admin-tenant', id], queryFn: () => getTenant(id) });
  const plans = useQuery({ queryKey: ['admin-plans'], queryFn: listPlans });
  // The catalogue rarely changes and the override dialog is unusable without it.
  const features = useQuery({
    queryKey: ['admin-features'],
    queryFn: listFeatures,
    staleTime: 60 * 60_000,
  });
  const sessions = useQuery({
    queryKey: ['support-sessions', id],
    queryFn: () => listSupportSessions(id),
  });

  function refresh(): void {
    void client.invalidateQueries({ queryKey: ['admin-tenant', id] });
    void client.invalidateQueries({ queryKey: ['admin-tenants'] });
    void client.invalidateQueries({ queryKey: ['support-sessions', id] });
  }

  const run = useMutation({
    mutationFn: async (reason: string) => {
      switch (action?.kind) {
        case 'suspend':
          return setTenantStatus(id, { status: 'SUSPENDED', reason });
        case 'restore':
          return setTenantStatus(id, { status: 'ACTIVE', reason });
        case 'readonly':
          return setTenantStatus(id, { status: 'READ_ONLY', reason });
        case 'plan':
          return changePlan(id, { planKey, reason });
        case 'trial':
          return extendTrial(id, { days, reason });
        case 'subscription':
          return setSubscriptionStatus(id, { status: subscriptionStatus, reason });
        case 'support': {
          const session = await startSupportSession(id, {
            reason,
            readOnly,
            durationMinutes: minutes,
          });

          // Shown exactly once. The token is a credential; it is not stored anywhere readable.
          setIssuedToken(session.token ?? null);

          return session;
        }
        case 'revoke-override':
          return revokeOverride(id, action.override.id);
        case 'feature-override':
          return overrideFeature(id, {
            featureKey: overrideKey.trim(),
            enabled: featureOn,
            reason,
            ...(overrideDays > 0 ? { endsAt: inDays(overrideDays) } : {}),
          });
        case 'limit-override':
          return overrideLimit(id, {
            limitKey: overrideKey.trim(),
            // An empty box means "no ceiling", which the API takes as null — not as zero, which
            // would be a limit of nothing at all.
            value: limitValue.trim() === '' ? null : Number(limitValue),
            reason,
            ...(overrideDays > 0 ? { endsAt: inDays(overrideDays) } : {}),
          });
        default:
          return undefined;
      }
    },
    onSuccess: () => {
      setAction(null);
      setError(null);
      setOverrideKey('');
      setLimitValue('');
      setOverrideDays(0);
      refresh();
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const stop = useMutation({
    mutationFn: (sessionId: string) => endSupportSession(sessionId),
    onSuccess: refresh,
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  if (tenant.error) {
    return (
      <div className="view">
        <ErrorState error={tenant.error} onRetry={() => void tenant.refetch()} />
      </div>
    );
  }

  if (!tenant.data) {
    return (
      <div className="view">
        <Loading />
      </div>
    );
  }

  const detail = tenant.data;
  const chosen = features.data?.features.find((feature) => feature.key === overrideKey);
  const entitlements = detail.entitlements;

  return (
    <div className="view">
      <PageHeader
        title={detail.name}
        description={detail.slug}
        actions={
          <Link className="button button--quiet button--compact" to="/admin">
            {t('record.backToList')}
          </Link>
        }
      />

      {error && !action && (
        <p className="alert" role="alert">
          {error}
        </p>
      )}

      {issuedToken && (
        <Panel title={t('admin.supportToken')}>
          <p className="muted small">{t('admin.supportTokenHint')}</p>
          <p>
            <code className="token">{issuedToken}</code>
          </p>
          <button
            type="button"
            className="button button--quiet button--compact"
            onClick={() => setIssuedToken(null)}
          >
            {t('admin.hideToken')}
          </button>
        </Panel>
      )}

      <div className="metrics">
        <div className="metric">
          <p className="metric__label">{t('crm.field.status')}</p>
          <p className="metric__value">
            <Badge tone={STATUS_TONE[detail.status] ?? 'neutral'}>
              {t(`admin.state.${detail.status}`)}
            </Badge>
          </p>
        </div>
        <Figure label={t('home.plan')} value={entitlements.planName} />
        <Figure
          label={t('admin.subscription')}
          value={t(`billing.status.${entitlements.status}`)}
        />
        <Figure label={t('admin.users')} value={formatNumber(detail.user_count)} />
      </div>

      <Panel title={t('admin.actions')}>
        <div className="toolbar">
          {detail.status === 'ACTIVE' ? (
            <>
              <button
                type="button"
                className="button button--quiet button--compact"
                onClick={() => setAction({ kind: 'readonly' })}
              >
                {t('admin.makeReadOnly')}
              </button>
              <button
                type="button"
                className="button button--quiet button--compact"
                onClick={() => setAction({ kind: 'suspend' })}
              >
                {t('admin.suspend')}
              </button>
            </>
          ) : (
            <button
              type="button"
              className="button button--quiet button--compact"
              onClick={() => setAction({ kind: 'restore' })}
            >
              {t('admin.restore')}
            </button>
          )}

          <button
            type="button"
            className="button button--quiet button--compact"
            onClick={() => {
              setPlanKey(entitlements.planKey);
              setAction({ kind: 'plan' });
            }}
          >
            {t('admin.changePlan')}
          </button>

          <button
            type="button"
            className="button button--quiet button--compact"
            onClick={() => setAction({ kind: 'trial' })}
          >
            {t('admin.extendTrial')}
          </button>

          <button
            type="button"
            className="button button--quiet button--compact"
            onClick={() => setAction({ kind: 'subscription' })}
          >
            {t('admin.setSubscription')}
          </button>

          <button
            type="button"
            className="button button--quiet button--compact"
            onClick={() => setAction({ kind: 'support' })}
          >
            {t('admin.openSupport')}
          </button>
        </div>
      </Panel>

      <div className="home-grid">
        <Panel title={t('billing.limits')}>
          <ul className="limit-list">
            {Object.entries(entitlements.limits).map(([key, value]) => (
              <li key={key}>
                <span className="limit-list__label">{t(`limit.${key}`)}</span>
                <span className="limit-list__value">
                  {key.endsWith('_bytes') ? formatBytes(value) : formatNumber(value)}
                </span>
              </li>
            ))}
          </ul>
        </Panel>

        <Panel title={t('billing.features')}>
          <ul className="feature-list">
            {entitlements.features.map((feature) => (
              <li key={feature}>
                <span className="feature-list__tick" aria-hidden="true">
                  ✓
                </span>
                {t(`feature.${feature}`)}
              </li>
            ))}
          </ul>
        </Panel>
      </div>

      <Panel
        title={t('admin.overrides')}
        actions={
          <span className="cell-inline">
            <button
              type="button"
              className="button button--quiet button--compact"
              onClick={() => {
                setOverrideKey('');
                setAction({ kind: 'feature-override' });
              }}
            >
              {t('admin.newFeatureOverride')}
            </button>
            <button
              type="button"
              className="button button--quiet button--compact"
              onClick={() => {
                setOverrideKey('');
                setLimitValue('');
                setAction({ kind: 'limit-override' });
              }}
            >
              {t('admin.newLimitOverride')}
            </button>
          </span>
        }
      >
        {detail.overrides.length === 0 ? (
          <p className="muted">{t('admin.noOverrides')}</p>
        ) : (
          <ul className="home-list">
            {detail.overrides.map((override) => (
              <li key={override.id}>
                <span className="cell-stack">
                  <span className="cell-inline">
                    <Badge tone="info">{override.kind}</Badge>
                    <code>{override.target_key}</code>
                    <span className="strong">{String(override.value)}</span>
                  </span>
                  <span className="muted small">{override.reason}</span>
                </span>

                <button
                  type="button"
                  className="button button--quiet button--compact"
                  onClick={() => setAction({ kind: 'revoke-override', override })}
                >
                  {t('admin.revokeOverride')}
                </button>
              </li>
            ))}
          </ul>
        )}
      </Panel>

      <InvoicesPanel tenantId={id} />

      <Panel title={t('admin.supportSessions')}>
        {(sessions.data?.sessions.length ?? 0) === 0 ? (
          <p className="muted">{t('admin.noSupportSessions')}</p>
        ) : (
          <ul className="home-list">
            {(sessions.data?.sessions ?? []).map((session) => (
              <li key={session.id}>
                <span className="cell-stack">
                  <span className="cell-inline">
                    <Badge tone={session.ended_at ? 'neutral' : 'warning'}>
                      {session.ended_at ? t('admin.sessionEnded') : t('admin.sessionOpen')}
                    </Badge>
                    <Badge tone={session.read_only ? 'neutral' : 'danger'}>
                      {session.read_only ? t('admin.readOnly') : t('admin.readWrite')}
                    </Badge>
                    <span className="muted small">{formatDateTime(session.expires_at)}</span>
                  </span>
                  <span className="muted small">{session.reason}</span>
                </span>

                {!session.ended_at && (
                  <button
                    type="button"
                    className="button button--quiet button--compact"
                    disabled={stop.isPending}
                    onClick={() => stop.mutate(session.id)}
                  >
                    {t('admin.endSession')}
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </Panel>

      <UsagePanel tenantId={id} />

      <Panel title={t('admin.history')}>
        {detail.subscription_events.length === 0 ? (
          <p className="muted">{t('admin.noHistory')}</p>
        ) : (
          <ol className="timeline">
            {detail.subscription_events.map((event) => (
              <li key={event.id} className="timeline__item">
                <span className="timeline__marker" aria-hidden="true" />
                <div className="timeline__body">
                  <div className="cell-inline">
                    <code>{event.type}</code>
                    <span className="muted small">{formatDateTime(event.occurred_at)}</span>
                  </div>
                </div>
              </li>
            ))}
          </ol>
        )}
      </Panel>

      {/* ----- the dialogs ----- */}

      <ConfirmAction
        open={action?.kind === 'feature-override'}
        title={t('admin.newFeatureOverride')}
        description={t('admin.featureOverrideBody')}
        confirmLabel={t('admin.grantOverride')}
        busy={run.isPending}
        error={error}
        onCancel={() => setAction(null)}
        onConfirm={(reason) => {
          if (overrideKey.trim()) run.mutate(reason);
        }}
      >
        <label className="stack__field">
          <span>{t('admin.featureKey')}</span>
          {/*
           * The whole catalogue, not the tenant's own features.
           *
           * It used to offer exactly the features the tenant already had — the one list that cannot
           * help, because an override exists precisely to grant something the plan does not
           * include. The features no plan carries at all are the ones somebody is looking for here,
           * and they were the only ones missing.
           */}
          <select
            className="select"
            value={overrideKey}
            onChange={(event) => setOverrideKey(event.target.value)}
          >
            <option value="">{t('admin.pickFeature')}</option>

            {features.data?.features.some((feature) => feature.only_by_override) && (
              <optgroup label={t('admin.onlyByOverride')}>
                {features.data.features
                  .filter((feature) => feature.only_by_override)
                  .map((feature) => (
                    <option key={feature.key} value={feature.key}>
                      {feature.name} — {feature.key}
                    </option>
                  ))}
              </optgroup>
            )}

            <optgroup label={t('admin.inSomePlan')}>
              {(features.data?.features ?? [])
                .filter((feature) => !feature.only_by_override)
                .map((feature) => (
                  <option key={feature.key} value={feature.key}>
                    {feature.name} — {feature.key}
                  </option>
                ))}
            </optgroup>
          </select>

          {/* What the tenant would get from a plan change, and what no plan can ever give them. */}
          {chosen && (
            <span className="muted small">
              {chosen.only_by_override
                ? t('admin.featureNoPlan')
                : t('admin.featureInPlans').replace('{plans}', chosen.plans.join(', '))}
            </span>
          )}
        </label>

        <label className="stack__field">
          <span>{t('admin.featureState')}</span>
          <select
            className="select"
            value={featureOn ? 'on' : 'off'}
            onChange={(event) => setFeatureOn(event.target.value === 'on')}
          >
            <option value="on">{t('admin.featureOn')}</option>
            <option value="off">{t('admin.featureOff')}</option>
          </select>
        </label>

        <ExpiryField days={overrideDays} onChange={setOverrideDays} />
      </ConfirmAction>

      <ConfirmAction
        open={action?.kind === 'limit-override'}
        title={t('admin.newLimitOverride')}
        description={t('admin.limitOverrideBody')}
        confirmLabel={t('admin.grantOverride')}
        busy={run.isPending}
        error={error}
        onCancel={() => setAction(null)}
        onConfirm={(reason) => {
          if (overrideKey.trim()) run.mutate(reason);
        }}
      >
        <label className="stack__field">
          <span>{t('admin.limitKey')}</span>
          <input
            list="tenant-limit-keys"
            value={overrideKey}
            placeholder="storage_bytes"
            onChange={(event) => setOverrideKey(event.target.value)}
          />
          <datalist id="tenant-limit-keys">
            {Object.keys(detail.entitlements.limits).map((limit) => (
              <option key={limit} value={limit} />
            ))}
          </datalist>
        </label>

        <label className="stack__field">
          <span>{t('admin.limitValue')}</span>
          <input
            type="number"
            min={0}
            value={limitValue}
            placeholder={t('admin.limitUnlimited')}
            onChange={(event) => setLimitValue(event.target.value)}
          />
          <span className="muted small">{t('admin.limitValueHint')}</span>
        </label>

        <ExpiryField days={overrideDays} onChange={setOverrideDays} />
      </ConfirmAction>

      <ConfirmAction
        open={action?.kind === 'suspend'}
        tone="danger"
        title={t('admin.suspendTitle')}
        description={t('admin.suspendBody').replace('{name}', detail.name)}
        confirmLabel={t('admin.suspend')}
        busy={run.isPending}
        error={error}
        onCancel={() => setAction(null)}
        onConfirm={(reason) => run.mutate(reason)}
      />

      <ConfirmAction
        open={action?.kind === 'readonly'}
        title={t('admin.readOnlyTitle')}
        description={t('admin.readOnlyBody')}
        confirmLabel={t('admin.makeReadOnly')}
        busy={run.isPending}
        error={error}
        onCancel={() => setAction(null)}
        onConfirm={(reason) => run.mutate(reason)}
      />

      <ConfirmAction
        open={action?.kind === 'restore'}
        title={t('admin.restoreTitle')}
        confirmLabel={t('admin.restore')}
        busy={run.isPending}
        error={error}
        onCancel={() => setAction(null)}
        onConfirm={(reason) => run.mutate(reason)}
      />

      <ConfirmAction
        open={action?.kind === 'plan'}
        title={t('admin.changePlanTitle')}
        confirmLabel={t('admin.changePlan')}
        busy={run.isPending}
        error={error}
        onCancel={() => setAction(null)}
        onConfirm={(reason) => run.mutate(reason)}
      >
        <label className="stack__field">
          <span>{t('admin.plan')}</span>
          <select
            className="select"
            value={planKey}
            onChange={(event) => setPlanKey(event.target.value)}
          >
            {(plans.data?.plans ?? []).map((plan) => (
              <option key={plan.key} value={plan.key}>
                {plan.name}
              </option>
            ))}
          </select>
        </label>
      </ConfirmAction>

      <ConfirmAction
        open={action?.kind === 'trial'}
        title={t('admin.extendTrialTitle')}
        confirmLabel={t('admin.extendTrial')}
        busy={run.isPending}
        error={error}
        onCancel={() => setAction(null)}
        onConfirm={(reason) => run.mutate(reason)}
      >
        <label className="stack__field">
          <span>{t('admin.days')}</span>
          <input
            type="number"
            min={1}
            max={365}
            value={days}
            onChange={(event) => setDays(Number(event.target.value))}
          />
        </label>
        {entitlements.trialEndsAt && (
          <p className="muted small">
            {t('admin.currentTrialEnds').replace('{date}', formatDate(entitlements.trialEndsAt))}
          </p>
        )}
      </ConfirmAction>

      <ConfirmAction
        open={action?.kind === 'subscription'}
        title={t('admin.setSubscriptionTitle')}
        confirmLabel={t('admin.setSubscription')}
        busy={run.isPending}
        error={error}
        onCancel={() => setAction(null)}
        onConfirm={(reason) => run.mutate(reason)}
      >
        <label className="stack__field">
          <span>{t('admin.subscription')}</span>
          <select
            className="select"
            value={subscriptionStatus}
            onChange={(event) => setSubscriptionStatusValue(event.target.value)}
          >
            {['ACTIVE', 'PAST_DUE', 'SUSPENDED', 'CANCELED', 'EXPIRED'].map((entry) => (
              <option key={entry} value={entry}>
                {t(`billing.status.${entry}`)}
              </option>
            ))}
          </select>
        </label>
      </ConfirmAction>

      <ConfirmAction
        open={action?.kind === 'support'}
        tone={readOnly ? 'default' : 'danger'}
        title={t('admin.openSupportTitle')}
        description={t('admin.openSupportBody')}
        confirmLabel={t('admin.openSupport')}
        busy={run.isPending}
        error={error}
        onCancel={() => setAction(null)}
        onConfirm={(reason) => run.mutate(reason)}
      >
        <label className="rail__toggle" style={{ padding: 0, color: 'inherit' }}>
          <input
            type="checkbox"
            checked={readOnly}
            onChange={(event) => setReadOnly(event.target.checked)}
          />
          {t('admin.readOnlySession')}
        </label>

        <label className="stack__field">
          <span>{t('admin.durationMinutes')}</span>
          <input
            type="number"
            min={1}
            max={480}
            value={minutes}
            onChange={(event) => setMinutes(Number(event.target.value))}
          />
        </label>
      </ConfirmAction>

      <ConfirmAction
        open={action?.kind === 'revoke-override'}
        title={t('admin.revokeOverrideTitle')}
        confirmLabel={t('admin.revokeOverride')}
        busy={run.isPending}
        error={error}
        onCancel={() => setAction(null)}
        onConfirm={(reason) => run.mutate(reason)}
      />
    </div>
  );
}

function Figure({ label, value }: { label: string; value: string }): React.JSX.Element {
  return (
    <div className="metric">
      <p className="metric__label">{label}</p>
      <p className="metric__value">{value}</p>
    </div>
  );
}

/**
 * How long an override lasts.
 *
 * Zero means "until somebody revokes it", which is the honest default: most overrides are a decision
 * about this customer, not a trial. The ones that *are* a trial are exactly the ones that should
 * expire on their own — an unlimited plan granted "for a couple of weeks" and never revoked is how a
 * price list stops meaning anything.
 */
function ExpiryField({
  days,
  onChange,
}: {
  days: number;
  onChange: (days: number) => void;
}): React.JSX.Element {
  return (
    <label className="stack__field">
      <span>{t('admin.overrideExpiry')}</span>
      <select
        className="select"
        value={days}
        onChange={(event) => onChange(Number(event.target.value))}
      >
        <option value={0}>{t('admin.overrideNoExpiry')}</option>
        {[7, 14, 30, 90].map((each) => (
          <option key={each} value={each}>
            {each} {t('admin.dayCount')}
          </option>
        ))}
      </select>
    </label>
  );
}

/** A date that many days from now, as the API wants it. */
function inDays(days: number): string {
  return new Date(Date.now() + days * 24 * 60 * 60 * 1000).toISOString();
}

/** The same pulse the tenant sees about itself, for the person about to call them. */
function UsagePanel({ tenantId }: { tenantId: string }): React.JSX.Element {
  const pulse = useQuery({
    queryKey: ['tenant-pulse', tenantId],
    queryFn: () => tenantPulse(tenantId),
  });

  return (
    <Panel title={t('admin.product.usage')}>
      {pulse.isLoading ? (
        <Loading />
      ) : pulse.error ? (
        <ErrorState error={pulse.error} onRetry={() => void pulse.refetch()} />
      ) : pulse.data ? (
        <div className="stack">
          <div className="cell-inline">
            <Badge tone={bandTone(pulse.data.health.band)}>
              {t(`admin.band.${pulse.data.health.band}`)}
            </Badge>
            <span className="muted small">
              {t('admin.product.people')}: {formatNumber(pulse.data.activeUsers)} /{' '}
              {formatNumber(pulse.data.members)} · {t('admin.product.activeDays')}:{' '}
              {formatNumber(pulse.data.activeDays)} · {t('admin.product.lastSeen')}:{' '}
              {pulse.data.lastEventAt
                ? formatDateTime(pulse.data.lastEventAt)
                : t('admin.product.never')}
            </span>
          </div>
          <div className="cell-inline">
            {pulse.data.activation.reached.map((m) => (
              <Badge key={m} tone="positive">
                {t(`admin.milestone.${m}`)}
              </Badge>
            ))}
            {pulse.data.activation.missing.map((m) => (
              <Badge key={m} tone="neutral">
                {t(`admin.milestone.${m}`)}
              </Badge>
            ))}
          </div>
          {pulse.data.byArea.length > 0 && (
            <p className="muted small">
              {pulse.data.byArea
                .map((row) => `${t(`usage.area.${row.area}`)} ${formatNumber(row.events)}`)
                .join(' · ')}
            </p>
          )}
        </div>
      ) : null}
    </Panel>
  );
}
